import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";

import { FieldLabel } from "@/components/ui/FieldLabel";

import { searchPlacesForSelect } from "@/api/places";
import {
  getPlaceSharePath,
  type PlaceSelectOption,
} from "@/core/services/types/place.types";

const inputClassName =
  "w-full wireframe-border bg-neutral-50 px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

interface EventPlaceFieldProps {
  selectedPlace: PlaceSelectOption | null;
  value: string;
  disabled: boolean;
  isPending: boolean;
  saveSpinner: ReactNode;
  fieldError?: string;
  error?: string;
  success: boolean;
  onChange: (value: string) => void;
  onSelectedPlaceChange?: (place: PlaceSelectOption | null) => void;
}

export function EventPlaceField({
  selectedPlace: initialSelectedPlace,
  value,
  disabled,
  isPending,
  saveSpinner,
  fieldError,
  error,
  success,
  onChange,
  onSelectedPlaceChange,
}: EventPlaceFieldProps) {
  const t = useTranslations("events.edit");
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedPlace, setSelectedPlace] = useState(initialSelectedPlace);
  const [isEditing, setIsEditing] = useState(!initialSelectedPlace);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PlaceSelectOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setSelectedPlace(initialSelectedPlace);
    setIsEditing(!initialSelectedPlace);
    onSelectedPlaceChange?.(initialSelectedPlace);
  }, [initialSelectedPlace, onSelectedPlaceChange]);

  const runSearch = useCallback(async (searchQuery: string) => {
    const result = await searchPlacesForSelect(searchQuery);

    if (!result.success) {
      setSearchError(result.error);
      setResults([]);
      setIsOpen(false);
      return;
    }

    setSearchError(null);
    setResults(result.places);
    setIsOpen(result.places.length > 0);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    if (!isEditing) return;

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    const timer = window.setTimeout(() => {
      void runSearch(trimmed).finally(() => setIsSearching(false));
    }, 300);

    return () => window.clearTimeout(timer);
  }, [query, isEditing, runSearch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(place: PlaceSelectOption) {
    setSelectedPlace(place);
    setIsEditing(false);
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setActiveIndex(-1);
    onSelectedPlaceChange?.(place);
    onChange(place.id);
  }

  function handleChangeSelection() {
    setIsEditing(true);
    setQuery("");
    setResults([]);
    setSearchError(null);
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || results.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) =>
        current < results.length - 1 ? current + 1 : 0,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current > 0 ? current - 1 : results.length - 1,
      );
      return;
    }

    if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      const place = results[activeIndex];
      if (place) handleSelect(place);
      return;
    }

    if (event.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }

  const showDropdown = isOpen && results.length > 0;
  const hasSelection = Boolean(value && selectedPlace);

  return (
    <section className="wireframe-border bg-white p-5 space-y-1.5" ref={containerRef}>
      <FieldLabel as="p" className="text-sm font-semibold text-whart-text" required>
        {t("placeLabel")}
      </FieldLabel>

      {!isEditing && hasSelection && selectedPlace ? (
        <div className="space-y-2">
          <div className="wireframe-border bg-neutral-50 px-3 py-2 text-sm text-whart-text">
            <Link
              to={getPlaceSharePath(selectedPlace.id)}
              className="block font-medium text-whart-text underline-offset-2 hover:underline"
            >
              {selectedPlace.name}
            </Link>
            {selectedPlace.city ? (
              <span className="block text-xs text-neutral-500">
                {selectedPlace.city}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleChangeSelection}
            disabled={disabled || isPending}
            className="text-xs text-neutral-600 underline hover:text-whart-text disabled:opacity-50"
          >
            {t("placeChangeSelection")}
          </button>
        </div>
      ) : (
        <div className="relative space-y-2">
          <input
            id="event-place"
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsOpen(true);
            }}
            onFocus={() => {
              if (results.length > 0) setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={t("placeSearchPlaceholder")}
            disabled={disabled || isPending}
            autoComplete="off"
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-busy={isPending || isSearching}
            aria-activedescendant={
              activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
            }
            className={inputClassName}
          />
          {isSearching ? (
            <p className="text-xs text-neutral-500">{t("placeSearching")}</p>
          ) : null}
          {!isSearching && query.trim().length > 0 && query.trim().length < 2 ? (
            <p className="text-xs text-neutral-500">{t("placeSearchHint")}</p>
          ) : null}
          {!isSearching &&
          query.trim().length >= 2 &&
          results.length === 0 &&
          !searchError ? (
            <p className="text-xs text-neutral-500">{t("placeSearchEmpty")}</p>
          ) : null}
          {searchError ? (
            <p role="alert" className="text-xs text-whart-urgent">
              {searchError}
            </p>
          ) : null}
          {showDropdown ? (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto border border-black/10 bg-white shadow-sm"
            >
              {results.map((place, index) => (
                <li key={place.id} role="presentation">
                  <button
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(place)}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-neutral-50 ${
                      index === activeIndex ? "bg-neutral-50" : ""
                    }`}
                  >
                    <span className="block font-medium text-whart-text">
                      {place.name}
                    </span>
                    {place.city ? (
                      <span className="block text-xs text-neutral-500">
                        {place.city}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {isPending ? (
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              {saveSpinner}
            </span>
          ) : null}
        </div>
      )}

      <FieldError message={fieldError} />
      {!success && error ? (
        <p role="alert" className="text-xs text-whart-urgent">
          {error}
        </p>
      ) : null}
      {success ? (
        <p role="status" className="text-xs text-emerald-700">
          {t("savePlaceSuccess")}
        </p>
      ) : null}
    </section>
  );
}
